/* eslint-disable import/no-unresolved */
/* eslint-disable import/export */

import 'swiper/css';
import 'swiper/css/navigation';

import type { FlexProps } from '@chakra-ui/react';
import { Box, Flex } from '@chakra-ui/react';
import { SlideButton } from '@ui/components/SlideButton';
import type { ReactNode } from 'react';
import { Children, useState } from 'react';
import type { SwiperProps } from 'swiper/react';
import { Swiper } from 'swiper/react';
import type { Swiper as SwiperClass } from 'swiper/types';

import { SlideIndicator } from './SlideIndicator';

export type SlidablePanelProps = Omit<FlexProps, 'title'> & {
  title: ReactNode | ((state: { swiperIndex: number }) => ReactNode);
  children: ReactNode;
  swipeDisabled?: boolean;
  explainer?: ReactNode;
  hideSlideButton?: boolean;
  allowTouchMove?: SwiperProps['grabCursor'];
  hideSlideIndicator?: boolean;
};

export const SlidablePanel = ({
  title,
  children,
  swipeDisabled,
  explainer,
  hideSlideButton,
  allowTouchMove = false,
  hideSlideIndicator = true,
  ...props
}: SlidablePanelProps) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const [swiperIndex, setSwiperIndex] = useState(0);

  children = Children.toArray(children).filter((v) => !!v);

  return (
    <>
      <Flex
        align="center"
        mb={explainer || !hideSlideIndicator ? '12px' : '24px'}
        {...props}
      >
        <Box flex="1" textStyle="h4Bold">
          {typeof title === 'function' ? title({ swiperIndex }) : title}
        </Box>
        <SlideButton
          hidden={hideSlideButton}
          isPrevDisabled={!swiperIndex || swipeDisabled}
          isNextDisabled={
            swiperIndex >= Children.count(children) - 1 || swipeDisabled
          }
          onPrev={() => swiper?.slidePrev()}
          onNext={() => swiper?.slideNext()}
        />
      </Flex>
      {!!explainer && (
        <Box color="text.light" textStyle="bodySmall" mb="24px">
          {explainer}
        </Box>
      )}
      {!hideSlideIndicator && (
        <SlideIndicator
          mb="12px"
          total={Children.count(children)}
          index={swiperIndex}
          onChange={(index) => {
            setSwiperIndex(index);
            swiper?.slideTo(index);
          }}
        />
      )}
      <Swiper
        onSwiper={setSwiper}
        grabCursor={allowTouchMove}
        slideToClickedSlide={false}
        loop={false}
        allowTouchMove={allowTouchMove}
        onSlideChange={({ realIndex }) => {
          setSwiperIndex(realIndex);
        }}
      >
        {children}
      </Swiper>
    </>
  );
};
